import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import AirportSelect from "@/components/AirportSelect";
import api from "@/lib/api";
import { toast } from "sonner";

const inp = "w-full bg-white border border-[#E5DFD2] focus:border-[#A47E2A] focus:outline-none px-4 py-2.5 rounded-sm text-[#1A1D24] transition-colors text-sm";

const emptyLeg = () => ({ departure_airport: "", arrival_airport: "", departure_datetime: "" });

export default function NewTrip() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    trip_type: "One-way",
    legs: [emptyLeg()],
    passenger_count: 2,
    aircraft_preference: "",
    luggage_notes: "",
    special_requirements: "",
    client_id: "temp",
  });

  // When trip_type changes, rebuild legs shape
  useEffect(() => {
    if (form.trip_type === "One-way" && form.legs.length !== 1) {
      setForm(f => ({ ...f, legs: [f.legs[0] || emptyLeg()] }));
    } else if (form.trip_type === "Round trip" && form.legs.length !== 2) {
      const outbound = form.legs[0] || emptyLeg();
      // Return leg auto-swaps departure/arrival
      const returnLeg = {
        departure_airport: outbound.arrival_airport || "",
        arrival_airport: outbound.departure_airport || "",
        departure_datetime: "",
      };
      setForm(f => ({ ...f, legs: [outbound, returnLeg] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.trip_type]);

  const submit = async () => {
    try {
      const res = await api.post("/trips", form);
      toast.success("Trip request submitted — Ops will confirm in 4 hours.");
      nav(`/portal/trips/${res.data.id}`);
    } catch {
      toast.error("Could not submit request.");
    }
  };

  const setLeg = (idx, key, val) => {
    const legs = [...form.legs];
    legs[idx] = { ...legs[idx], [key]: val };
    // If round trip and outbound changes, auto-sync return leg airports
    if (form.trip_type === "Round trip" && idx === 0) {
      if (key === "departure_airport") legs[1] = { ...legs[1], arrival_airport: val };
      if (key === "arrival_airport") legs[1] = { ...legs[1], departure_airport: val };
    }
    setForm({ ...form, legs });
  };

  const addLeg = () => setForm({ ...form, legs: [...form.legs, emptyLeg()] });
  const removeLeg = (idx) => setForm({ ...form, legs: form.legs.filter((_, i) => i !== idx) });

  return (
    <DashboardLayout role="client">
      <div className="max-w-[1000px] mx-auto px-8 py-10">
        <div className="as-overline">New Trip Request</div>
        <h1 className="as-serif text-5xl font-light mt-2" data-testid="new-trip-title">Tell us the mission.</h1>

        {/* Stepper */}
        <div className="flex items-center gap-3 mt-8 mb-10">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-sm flex items-center justify-center as-mono text-xs ${step >= s ? "bg-[#A47E2A] text-white" : "border border-[#E5DFD2] text-[#756E64]"}`} data-testid={`step-${s}`}>{s}</div>
              {s < 3 && <div className={`w-16 h-px ${step > s ? "bg-[#A47E2A]" : "bg-[#E5DFD2]"}`} />}
            </div>
          ))}
          <span className="as-mono text-[10px] uppercase tracking-widest text-[#756E64] ml-4">
            {step === 1 ? "Route" : step === 2 ? "Aircraft & Passengers" : "Preferences & Submit"}
          </span>
        </div>

        <div className="as-card p-8">
          {step === 1 && (
            <div className="space-y-6" data-testid="step-1-content">
              <div>
                <div className="as-mono text-[10px] uppercase tracking-widest text-[#756E64] mb-3">Trip Type</div>
                <div className="grid grid-cols-3 gap-3">
                  {["One-way", "Round trip", "Multi-leg"].map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setForm({ ...form, trip_type: t })}
                      className={`px-4 py-4 rounded-sm border transition-all as-mono text-[11px] uppercase tracking-widest ${form.trip_type === t ? "border-[#A47E2A] text-[#A47E2A] bg-[#A47E2A]/5" : "border-[#E5DFD2] text-[#756E64] hover:border-[#1A1D24]/30"}`}
                      data-testid={`trip-type-${t.toLowerCase().replace(" ", "-")}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              {form.legs.map((leg, idx) => {
                const legLabel = form.trip_type === "Round trip"
                  ? (idx === 0 ? "Outbound" : "Return")
                  : (form.trip_type === "Multi-leg" ? `Leg ${idx + 1}` : "Flight");
                return (
                  <div key={idx} className="border-t border-[#E5DFD2] pt-6">
                    <div className="flex items-center justify-between mb-3">
                      <div className="as-mono text-[10px] uppercase tracking-widest text-[#A47E2A]">{legLabel}</div>
                      {form.trip_type === "Multi-leg" && form.legs.length > 1 && (
                        <button type="button" onClick={() => removeLeg(idx)} className="text-xs text-[#756E64] hover:text-red-600" data-testid={`remove-leg-${idx}`}>Remove</button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <label>
                        <div className="as-mono text-[10px] uppercase tracking-widest text-[#756E64] mb-2">Departure</div>
                        <AirportSelect
                          value={leg.departure_airport}
                          onChange={(v) => setLeg(idx, "departure_airport", v)}
                          testId={`leg-${idx}-dep`}
                          placeholder="Select departure airport"
                        />
                      </label>
                      <label>
                        <div className="as-mono text-[10px] uppercase tracking-widest text-[#756E64] mb-2">Arrival</div>
                        <AirportSelect
                          value={leg.arrival_airport}
                          onChange={(v) => setLeg(idx, "arrival_airport", v)}
                          testId={`leg-${idx}-arr`}
                          placeholder="Select arrival airport"
                        />
                      </label>
                      <label>
                        <div className="as-mono text-[10px] uppercase tracking-widest text-[#756E64] mb-2">Departure DateTime</div>
                        <input
                          required
                          type="datetime-local"
                          value={leg.departure_datetime.slice(0, 16)}
                          onChange={(e) => setLeg(idx, "departure_datetime", e.target.value ? new Date(e.target.value).toISOString() : "")}
                          className={inp}
                          data-testid={`leg-${idx}-dt`}
                        />
                      </label>
                    </div>
                  </div>
                );
              })}
              {form.trip_type === "Multi-leg" && (
                <button type="button" onClick={addLeg} className="as-btn-ghost px-4 py-2 rounded-sm as-mono text-[10px] uppercase tracking-widest" data-testid="add-leg">+ Add leg</button>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="step-2-content">
              <label>
                <div className="as-mono text-[10px] uppercase tracking-widest text-[#756E64] mb-2">Passengers</div>
                <input type="number" min="1" value={form.passenger_count} onChange={(e) => setForm({ ...form, passenger_count: parseInt(e.target.value) })} className={inp} data-testid="pax-count" />
              </label>
              <label>
                <div className="as-mono text-[10px] uppercase tracking-widest text-[#756E64] mb-2">Aircraft Preference</div>
                <select value={form.aircraft_preference} onChange={(e) => setForm({ ...form, aircraft_preference: e.target.value })} className={inp} data-testid="aircraft-pref">
                  <option value="">Ops recommendation</option>
                  <option>Phenom 300E</option>
                  <option>Citation Latitude</option>
                  <option>Challenger 350</option>
                  <option>Gulfstream G650ER</option>
                  <option>Global 7500</option>
                  <option>ACJ319neo</option>
                </select>
              </label>
              <label className="md:col-span-2">
                <div className="as-mono text-[10px] uppercase tracking-widest text-[#756E64] mb-2">Luggage Notes</div>
                <textarea rows="2" value={form.luggage_notes} onChange={(e) => setForm({ ...form, luggage_notes: e.target.value })} className={inp} placeholder="Number of suitcases, oversized items…" data-testid="luggage" />
              </label>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6" data-testid="step-3-content">
              <label className="block">
                <div className="as-mono text-[10px] uppercase tracking-widest text-[#756E64] mb-2">Special Requirements</div>
                <textarea rows="6" value={form.special_requirements} onChange={(e) => setForm({ ...form, special_requirements: e.target.value })} className={inp} placeholder="Catering, security detail, ground transport, discretion, medical…" data-testid="special-req" />
              </label>
              <div className="as-card p-5 bg-[#F1EDE4] border-[#E5DFD2]">
                <div className="as-overline mb-3">Review</div>
                <div className="text-sm space-y-1 text-[#756E64]">
                  <div><span className="text-[#1A1D24]">Type:</span> {form.trip_type}</div>
                  {form.legs.map((l, i) => (
                    <div key={i}>
                      <span className="text-[#1A1D24]">
                        {form.trip_type === "Round trip" ? (i === 0 ? "Outbound:" : "Return:") : `Leg ${i + 1}:`}
                      </span>{" "}
                      {l.departure_airport} → {l.arrival_airport} on {l.departure_datetime.slice(0, 16).replace("T", " ")}
                    </div>
                  ))}
                  <div><span className="text-[#1A1D24]">Passengers:</span> {form.passenger_count}</div>
                  <div><span className="text-[#1A1D24]">Aircraft:</span> {form.aircraft_preference || "Ops recommendation"}</div>
                </div>
              </div>
            </div>
          )}

          <div className="mt-8 flex items-center justify-between">
            <button
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              disabled={step === 1}
              className="as-btn-ghost px-5 py-3 rounded-sm as-mono text-[10px] uppercase tracking-widest disabled:opacity-30"
              data-testid="step-back"
            >
              Back
            </button>
            {step < 3 ? (
              <button onClick={() => setStep((s) => s + 1)} className="as-btn-gold px-6 py-3 rounded-sm as-mono text-[10px] uppercase tracking-widest" data-testid="step-next">Continue</button>
            ) : (
              <button onClick={submit} className="as-btn-gold px-6 py-3 rounded-sm as-mono text-[10px] uppercase tracking-widest" data-testid="submit-trip">Submit Request</button>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
